import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FitnessCenter,
  TrackChanges,
  Psychology,
  SportsScore,
  Timer,
  MenuBook,
  AutoStories,
  ExpandMore,
  ExpandLess,
} from "@mui/icons-material";

import { exdetails } from "../../components/funtionsBD/Mainservice";
import { AddHeader } from "../../components/AddHeader";
import { ip_back } from "../../config";
import Loader from "../../components/Loading/Loader";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedField } from "../../utils/localizedCatalogFields";
import "../Exerciseadd/Exercise.css";
import "./ExerciseDetails.css";

const LEVEL_COLORS = [
  "#4caf50", "#8bc34a", "#cddc39", "#ffc107", "#ff9800",
  "#ff5722", "#e91e63", "#9c27b0", "#673ab7", "#3f51b5",
];

const CardioIndicator = ({ level }) => {
  if (!level && level !== 0) return null;
  const maxLevel = 5;
  const filled = Math.min(level, maxLevel);
  return (
    <div className="excard-cardio">
      {Array.from({ length: maxLevel }, (_, i) => (
        <span
          key={i}
          className={`excard-cardio__dot ${i < filled ? "excard-cardio__dot--filled" : ""}`}
          style={i < filled ? { backgroundColor: LEVEL_COLORS[Math.min(i, LEVEL_COLORS.length - 1)] } : {}}
        />
      ))}
    </div>
  );
};

const ThemeBadge = ({ theme, lng }) => {
  const name = localizedField(theme, "theme_name", lng) || "—";
  const level = theme.level || theme.the_lavel || 0;
  const color = LEVEL_COLORS[Math.min(level - 1, LEVEL_COLORS.length - 1)] || "#90a4ae";

  return (
    <span className="excard-badge excard-badge--theme" style={{ borderColor: color }}>
      <span className="excard-badge__label">{name}</span>
      <span className="excard-badge__level" style={{ backgroundColor: color }}>
        L{level}
      </span>
    </span>
  );
};

const DetailSection = ({ icon: Icon, iconColor, title, children, collapsible }) => {
  const [open, setOpen] = useState(!collapsible);

  if (!children) return null;

  return (
    <div className="exdetail-section">
      <div
        className={`exdetail-section__header ${collapsible ? "exdetail-section__header--clickable" : ""}`}
        onClick={collapsible ? () => setOpen((p) => !p) : undefined}
      >
        <Icon className="exdetail-section__icon" style={iconColor ? { color: iconColor } : {}} />
        <span className="exdetail-section__title">{title}</span>
        {collapsible && (
          open ? <ExpandLess className="exdetail-section__chevron" /> : <ExpandMore className="exdetail-section__chevron" />
        )}
      </div>
      {open && <div className="exdetail-section__body">{children}</div>}
    </div>
  );
};

export const ExerciseDetail = () => {
  const [exerciseInfo, setExerciseInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const lng = currentAppLanguage(i18n);

  useEffect(() => {
    getdetails();
  }, []);

  const getdetails = async () => {
    try {
      const params = { te_id: location.state.id };
      const response = await exdetails(params);
      setExerciseInfo(response.data.data);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <><AddHeader title={t("exdetails")} /><Loader /></>;
  if (!exerciseInfo) return <><AddHeader title={t("exdetails")} /><p className="error_msg">{t("te_not_found")}</p></>;

  const name = localizedField(exerciseInfo, "ex_name", lng) || "—";
  const objective = localizedField(exerciseInfo, "ex_objective", lng);
  const instruction = localizedField(exerciseInfo, "ex_instruction", lng);
  const process = localizedField(exerciseInfo, "ex_process", lng);
  const storytelling = localizedField(exerciseInfo, "ex_storytelling", lng);

  const typeName = localizedField(exerciseInfo.type_details, "type_name", lng);
  const isBlocksOfCompetency = (exerciseInfo.type_details?.type_name_en || "") === "Blocks of competency";
  const cbName = localizedField(exerciseInfo.cb_details, "cb_name", lng);
  const subTypeName =
    isBlocksOfCompetency && cbName
      ? cbName
      : localizedField(exerciseInfo.sub_type_details, "sub_type_name", lng);

  const themes = exerciseInfo.themes || [];
  const skills = exerciseInfo.skills || [];
  const focusList = exerciseInfo.focus || [];
  const cardioLev = exerciseInfo.ex_cardio_lev;

  return (
    <>
      <AddHeader title={t("exdetails")} />

      <div className="exdetail-page">
        {/* Main card */}
        <div className="exdetail-card">
          {/* Hero header */}
          <div className="exdetail-hero">
            {exerciseInfo.sub_type_details?.sub_type_image_normal && (
              <div className="exdetail-hero__img">
                <img src={ip_back + exerciseInfo.sub_type_details.sub_type_image_normal} alt="" />
              </div>
            )}
            <div className="exdetail-hero__info">
              <h1 className="exdetail-hero__name">{name}</h1>
              <div className="exdetail-hero__tags">
                {typeName && (
                  <span className="excard-tag excard-tag--type">{typeName}</span>
                )}
                {subTypeName && (
                  <span className={`excard-tag ${isBlocksOfCompetency && cbName ? "excard-tag--cb" : "excard-tag--subtype"}`} title={isBlocksOfCompetency && cbName ? (t("competencyblock") || "Competency Block") : undefined}>
                    {subTypeName}
                  </span>
                )}
                {exerciseInfo.isTerminal === "Yes" && (
                  <span className="excard-tag excard-tag--subtype">BC</span>
                )}
                {cardioLev != null && (
                  <span className="excard-tag excard-tag--cardio" title={t("cardioLevel")}>
                    <FitnessCenter style={{ fontSize: 13 }} />
                    <CardioIndicator level={cardioLev} />
                  </span>
                )}
              </div>
              <div className="exdetail-hero__meta">
                <span className="exdetail-hero__duration">
                  <Timer style={{ fontSize: 16 }} />
                  {exerciseInfo.te_duration} {t("min")}
                </span>
                {exerciseInfo.te_state && (
                  <span className="exdetail-hero__state">
                    {localizedField(exerciseInfo.te_state, "st_name", lng)}
                  </span>
                )}
              </div>
              {process && (
                <p className="exdetail-hero__description">{process}</p>
              )}
            </div>
          </div>

          {/* Themes */}
          {themes.length > 0 && (
            <div className="exdetail-badges-row">
              <TrackChanges className="exdetail-badges-row__icon" style={{ color: "#5c6bc0" }} />
              <span className="exdetail-badges-row__label">{t("themes")}</span>
              <div className="exdetail-badges-row__list">
                {themes.map((theme, idx) => (
                  <ThemeBadge key={idx} theme={theme} lng={lng} />
                ))}
              </div>
            </div>
          )}

          {/* Skills */}
          {skills.length > 0 && (
            <div className="exdetail-badges-row">
              <Psychology className="exdetail-badges-row__icon" style={{ color: "#26a69a" }} />
              <span className="exdetail-badges-row__label">{t("skills")}</span>
              <div className="exdetail-badges-row__list">
                {skills.map((skill, idx) => (
                  <span key={idx} className="excard-badge excard-badge--skill">
                    {localizedField(skill, "skill_name", lng) || "—"}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Focus is shown as a DetailSection below */}
        </div>

        {/* Content sections */}
        <div className="exdetail-sections">
          {objective && (
            <DetailSection icon={SportsScore} iconColor="#1565c0" title={t("objective")}>
              <p className="exdetail-text">{objective}</p>
            </DetailSection>
          )}

          {focusList.length > 0 && (
            <DetailSection icon={TrackChanges} iconColor="#e65100" title={t("focus")}>
              {focusList.map((f, idx) => {
                const focusText = localizedField(f, "focus_description", lng) || "—";
                return <p key={idx} className="exdetail-text exdetail-text--focus-item">{focusText}</p>;
              })}
            </DetailSection>
          )}

          {instruction && (
            <DetailSection icon={MenuBook} iconColor="#0877BC" title={t("instruction")}>
              <p className="exdetail-text">{instruction}</p>
            </DetailSection>
          )}

          {storytelling && (
            <DetailSection icon={AutoStories} iconColor="#6a1b9a" title={t("storytelling") || "Storytelling"} collapsible>
              <p className="exdetail-text">{storytelling}</p>
            </DetailSection>
          )}
        </div>

        {/* Auto-evaluation for players */}
        {exerciseInfo.auto_evaluation && exerciseInfo.auto_evaluation.already_evaluated && (
          <div className="exdetail-eval">
            <h4 className="exdetail-eval__title">{t("autoEvaluation") || "Auto-Evaluation"}</h4>
            <span className="exdetail-eval__badge">{t("completed") || "Completed"}</span>
          </div>
        )}

        {/* Player details for coaches */}
        {exerciseInfo.player_details && exerciseInfo.player_details.length > 0 && (
          <div className="exdetail-players">
            <h4 className="exdetail-players__title">{t("playerEvaluations") || "Player Evaluations"}</h4>
            <div className="exdetail-players__list">
              {exerciseInfo.player_details.map((p, idx) => (
                <div key={idx} className="exdetail-players__item">
                  <span className="exdetail-players__name">{p.player_name}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};
